/**
 * MmpAnalyticsPage.tsx — Activation Funnel Dashboard
 * 6-phase, 50-step activation funnel. Backed by GET /api/admin/activation-funnel.
 *
 * Page order (top → bottom), one cohesive vertical dashboard:
 *   Header (crumb, title, intro, date-range toggle)
 *   1. Milestone Funnel        (MilestoneFunnel)
 *   2. Acquisition Mix         (AcquisitionMix)
 *   3. Phase-to-Phase Conversion (PhaseConversionStaircase)
 *   4. Leak Ledger             (LeakLedger)
 *   Phase Flow — Heat Strip    (existing Heatstrip / Cascade funnel card, retained)
 *
 * Steps with no telemetry render as "Awaiting telemetry" (never fake data).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Paper, Skeleton, Tooltip as MuiTooltip,
} from '@mui/material';
import {
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';

import apiService from '../../services/api';
import TimeRangeFilter from '../common/TimeRangeFilter';
import useTimeRangeParam from '../common/useTimeRangeParam';
import { TimeRangeValue, resolveRangeYmd, toApiToken } from '../common/timeRange';
import {
  getBandColor, getBandTextColor, fmtLeak, fmtDwell, fmtPctOfTotal,
  BAND_LEGEND, PHASE_COLORS, stepHasData, resolveRetentionRatio,
  FONT, FONT_H, formatShares, fmtWindowHours,
} from './funnelBands';
import { StepData, PhaseData, AuthMethod, FunnelResponse } from './activationTypes';
import MilestoneFunnel from './MilestoneFunnel';
import JourneyFunnelSummaryStrip from './JourneyFunnelSummaryStrip';
import AcquisitionMix from './AcquisitionMix';
import PhaseConversionStaircase from './PhaseConversionStaircase';
import LeakLedger from './LeakLedger';

// ─── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Single time filter drives both cohort date range and Leak Ledger staleness.
 * Staleness = length of the selected period (capped at 30d / 720h to match API).
 *
 * NOT `resolveRange` from the shared module: this is a staleness window in
 * hours, clamped to the API's own 24h–720h band, not a calendar span.
 */
function resolveWindowHours(value: TimeRangeValue): number {
  switch (value.type) {
    case 'today': return 24;
    case '3d': return 72;
    case '7d': return 168;
    case '14d': return 336;
    case '30d': return 720;
    case '90d': return 720;
    case 'custom': {
      const { from, to } = value;
      const ms = Date.parse(to) - Date.parse(from);
      if (Number.isFinite(ms) && ms >= 0) {
        const days = Math.max(1, Math.round(ms / 86400000) + 1);
        return Math.min(720, Math.max(24, days * 24));
      }
      return 168;
    }
    default: return 168;
  }
}

// Content width — wide enough to use the dashboard canvas without large side gutters.
const PAGE_MAX_WIDTH = 1600;

// ─── Band Legend ───────────────────────────────────────────────────────────────

const BandLegend: React.FC = () => (
  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
    {BAND_LEGEND.map(b => (
      <Box key={b.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: b.color, flexShrink: 0 }} />
        <Typography sx={{ fontFamily: FONT, fontSize: '9.5px', color: '#6B7280' }}>{b.label}</Typography>
      </Box>
    ))}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: '#E5E7EB', flexShrink: 0 }} />
      <Typography sx={{ fontFamily: FONT, fontSize: '9.5px', color: '#9CA3AF' }}>Awaiting telemetry</Typography>
    </Box>
  </Box>
);

// ─── Auth Method Breakdown (inline under Registration Completed) ────────────────

const AuthMethodBreakdown: React.FC<{ methods: AuthMethod[]; funnelTop: number }> = ({ methods, funnelTop }) => {
  if (!methods.length) return null;
  // Same denominator AND same rounding as the Acquisition Mix section
  // (formatShares, largest-remainder). This panel used to Math.round while
  // section 2 largest-remainder'd — the same provider printed 43% here and
  // 42% there on one page.
  const shares = formatShares(methods.map(m => m.value), funnelTop);
  return (
    <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid #E5E7EB' }}>
      <Typography sx={{ fontFamily: FONT, fontSize: '9px', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
        Auth method breakdown
      </Typography>
      {methods.map((m, idx) => (
        <Box key={m.key ?? m.method} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '10px', color: '#374151' }}>{m.method}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: '10px', color: '#6B7280' }}>
            {m.value.toLocaleString()} {funnelTop > 0 ? `(${shares[idx]})` : ''}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ─── Heatstrip Step Cell ───────────────────────────────────────────────────────

const HeatCell: React.FC<{
  step: StepData;
  funnelTop: number;
  authMethods: AuthMethod[];
  isRegistration: boolean;
}> = ({ step, funnelTop, authMethods, isRegistration }) => {
  const hasData = stepHasData(step);
  const retention = hasData ? resolveRetentionRatio(step) : null;
  const bg = getBandColor(retention);
  const textColor = getBandTextColor(retention);
  const muted = !hasData;

  return (
    <Box sx={{
      bgcolor: bg,
      borderRadius: '8px',
      p: '8px 10px',
      width: '100%',
      minWidth: 0,
      opacity: muted ? 0.65 : 1,
      border: muted ? '1px dashed #D1D5DB' : 'none',
    }}>
      <Typography sx={{ fontFamily: FONT, fontSize: '9px', fontWeight: 700, color: muted ? '#9CA3AF' : textColor, opacity: 0.75, mb: 0.25, lineHeight: 1.2 }}>
        {step.i}. {step.tag === 'branch' ? '↳ ' : step.tag === 'attribute' ? '◦ ' : ''}{step.name}
      </Typography>

      {muted ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '10px', color: '#9CA3AF', fontStyle: 'italic' }}>
            Awaiting telemetry
          </Typography>
          <MuiTooltip title="This step's event has never fired in production — it is instrumented in config but no telemetry has arrived." placement="top">
            <InfoIcon sx={{ fontSize: 12, color: '#9CA3AF' }} />
          </MuiTooltip>
        </Box>
      ) : (
        <>
          <Typography sx={{ fontFamily: FONT_H, fontSize: '18px', fontWeight: 700, color: textColor, lineHeight: 1.1, mb: 0.25 }}>
            {step.value !== null ? step.value.toLocaleString() : '—'}
          </Typography>
          {step.tag === null && (
            <Typography sx={{ fontFamily: FONT, fontSize: '9.5px', color: textColor, opacity: 0.85, lineHeight: 1.3 }}>
              {fmtLeak(step.lost, step.lost_step_pct)}
            </Typography>
          )}
          <Typography sx={{ fontFamily: FONT, fontSize: '9.5px', color: textColor, opacity: 0.75 }}>
            {fmtPctOfTotal(step.value, funnelTop)} · ⏱ {fmtDwell(step.median_dwell_seconds)}
          </Typography>
          {step.stale_days != null && (
            <Typography sx={{ fontFamily: FONT, fontSize: '9px', color: textColor, opacity: 0.9, fontStyle: 'italic' }}>
              ⚠ no events in {step.stale_days}d
            </Typography>
          )}
        </>
      )}

      {isRegistration && !muted && authMethods.length > 0 && (
        <AuthMethodBreakdown methods={authMethods} funnelTop={funnelTop} />
      )}
    </Box>
  );
};

// ─── Heatstrip View ────────────────────────────────────────────────────────────

const FunnelHeatstrip: React.FC<{
  phases: PhaseData[];
  funnelTop: number;
  authMethods: AuthMethod[];
}> = ({ phases, funnelTop, authMethods }) => (
  <Box>
    {phases.map(phase => (
      <Box key={phase.p} sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: PHASE_COLORS[phase.p] }} />
          <Typography sx={{ fontFamily: FONT, fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#374151' }}>
            Phase {phase.display_index ?? phase.p} — {phase.name}
          </Typography>
          {/* "phase completion" = last trunk step ÷ first trunk step WITHIN the
              phase. Named distinctly from the Staircase's between-phase
              survival — the two used to share the label "conv." A value over
              100 means the phase's steps are not a strict chain for every
              Member (e.g. web signups never fire the app-open entry step) —
              the D3.2 invariant says show a data-quality marker, never an
              impossible percentage. */}
          {phase.phase_conv_pct !== null && (
            phase.phase_conv_pct > 100 ? (
              <MuiTooltip title={`Completion computes to ${phase.phase_conv_pct}% — the phase's last step counts Members its first step misses (not every Member fires the entry event). A data-quality signal, not a rate.`} placement="top">
                <Typography sx={{ fontFamily: FONT, fontSize: '10px', color: '#B45309', cursor: 'help' }}>
                  ⚠ data check — entry step undercounts
                </Typography>
              </MuiTooltip>
            ) : (
              <Typography sx={{ fontFamily: FONT, fontSize: '10px', color: '#9CA3AF' }}>
                {phase.phase_conv_pct}% phase completion
              </Typography>
            )
          )}
        </Box>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))',
          gap: 1,
        }}>
          {phase.steps.map(step => (
            <HeatCell
              key={step.i}
              step={step}
              funnelTop={funnelTop}
              authMethods={authMethods}
              isRegistration={Boolean(step.is_registration)}
            />
          ))}
        </Box>
      </Box>
    ))}
  </Box>
);

// ─── Cascade Row ───────────────────────────────────────────────────────────────

const CascadeRow: React.FC<{
  step: StepData;
  funnelTop: number;
  authMethods: AuthMethod[];
  isRegistration: boolean;
}> = ({ step, funnelTop, authMethods, isRegistration }) => {
  const hasData = stepHasData(step);
  const retention = hasData ? resolveRetentionRatio(step) : null;
  const muted = !hasData;
  const bg = getBandColor(retention);
  const barWidth = (funnelTop > 0 && step.value !== null)
    ? Math.max(2, (step.value / funnelTop) * 100)
    : 0;
  const lostWidth = (funnelTop > 0 && step.lost !== null)
    ? Math.min(100 - barWidth, (step.lost / funnelTop) * 100)
    : 0;

  const indent = step.tag === 'branch' ? 24 : step.tag === 'attribute' ? 16 : 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, pl: `${indent}px` }}>
        {/* Step label */}
        <Box sx={{ width: 220, flexShrink: 0 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '10.5px', color: muted ? '#9CA3AF' : '#111827', fontWeight: step.tag === null ? 600 : 400 }}>
            {step.tag === 'branch' ? '↳ ' : step.tag === 'attribute' ? '◦ ' : ''}
            {step.name}
          </Typography>
        </Box>

        {/* Bar area */}
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative', height: 20, display: 'flex', alignItems: 'center' }}>
          {muted ? (
            <Typography sx={{ fontFamily: FONT, fontSize: '10px', color: '#9CA3AF', fontStyle: 'italic' }}>
              Awaiting telemetry
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', height: 16, width: '100%', borderRadius: '3px', overflow: 'hidden', bgcolor: '#F3F4F6' }}>
              <Box sx={{ width: `${barWidth}%`, bgcolor: bg, transition: 'width 0.3s' }} />
              {step.tag === null && lostWidth > 0 && (
                <Box sx={{
                  width: `${lostWidth}%`,
                  bgcolor: 'transparent',
                  backgroundImage: 'repeating-linear-gradient(45deg, #FCA5A5 0, #FCA5A5 2px, transparent 0, transparent 50%)',
                  backgroundSize: '6px 6px',
                }} />
              )}
            </Box>
          )}
        </Box>

        {/* Value */}
        <Box sx={{ width: 60, flexShrink: 0, textAlign: 'right' }}>
          {!muted && (
            <Typography sx={{ fontFamily: FONT, fontSize: '11px', fontWeight: 700, color: '#111827' }}>
              {step.value !== null ? step.value.toLocaleString() : '—'}
            </Typography>
          )}
        </Box>

        {/* Leak readout */}
        <Box sx={{ width: 200, flexShrink: 0 }}>
          {!muted && step.tag === null && (
            <Typography sx={{ fontFamily: FONT, fontSize: '9.5px', color: '#7D1935' }}>
              {fmtLeak(step.lost, step.lost_step_pct)}
            </Typography>
          )}
          {/* Same stale marker as the heat strip — the two views derive from
              one array and must agree on every field they both show. */}
          {!muted && step.stale_days != null && (
            <Typography sx={{ fontFamily: FONT, fontSize: '9px', color: '#B45309', fontStyle: 'italic' }}>
              ⚠ no events in {step.stale_days}d
            </Typography>
          )}
        </Box>
      </Box>

      {isRegistration && !muted && authMethods.length > 0 && (
        <Box sx={{ pl: `${indent + 220 + 12}px`, pb: 1 }}>
          <AuthMethodBreakdown methods={authMethods} funnelTop={funnelTop} />
        </Box>
      )}
    </Box>
  );
};

const FunnelCascade: React.FC<{
  phases: PhaseData[];
  funnelTop: number;
  authMethods: AuthMethod[];
}> = ({ phases, funnelTop, authMethods }) => (
  <Box>
    {/* Column headers */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1, borderBottom: '1px solid #E5E7EB', mb: 1 }}>
      <Box sx={{ width: 220, flexShrink: 0 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9CA3AF' }}>Step</Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9CA3AF' }}>Retention bar</Typography>
      </Box>
      <Box sx={{ width: 60, flexShrink: 0, textAlign: 'right' }}>
        <Typography sx={{ fontFamily: FONT, fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9CA3AF' }}>Count</Typography>
      </Box>
      <Box sx={{ width: 200, flexShrink: 0 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9CA3AF' }}>Leak</Typography>
      </Box>
    </Box>

    {phases.map(phase => (
      <Box key={phase.p} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, py: 0.5, bgcolor: '#F9FAFB', borderRadius: '6px', px: 1 }}>
          <Box sx={{ width: 4, height: 12, borderRadius: 1, bgcolor: PHASE_COLORS[phase.p], flexShrink: 0 }} />
          <Typography sx={{ fontFamily: FONT, fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#374151' }}>
            Phase {phase.display_index ?? phase.p} — {phase.name}
          </Typography>
          {phase.phase_conv_pct !== null && (
            phase.phase_conv_pct > 100 ? (
              <MuiTooltip title={`Completion computes to ${phase.phase_conv_pct}% — the phase's last step counts Members its first step misses. A data-quality signal, not a rate.`} placement="top">
                <Typography sx={{ fontFamily: FONT, fontSize: '9.5px', color: '#B45309', cursor: 'help' }}>
                  (⚠ data check — entry step undercounts)
                </Typography>
              </MuiTooltip>
            ) : (
              <Typography sx={{ fontFamily: FONT, fontSize: '9.5px', color: '#9CA3AF' }}>
                ({phase.phase_conv_pct}% phase completion)
              </Typography>
            )
          )}
        </Box>
        {phase.steps.map(step => (
          <CascadeRow
            key={step.i}
            step={step}
            funnelTop={funnelTop}
            authMethods={authMethods}
            isRegistration={Boolean(step.is_registration)}
          />
        ))}
      </Box>
    ))}
  </Box>
);

// ─── Page ──────────────────────────────────────────────────────────────────────

const MmpAnalyticsPage: React.FC = () => {
  const [range, setRange] = useTimeRangeParam({ type: '30d' });
  const [view, setView] = useState<'heatstrip' | 'cascade'>('heatstrip');
  const [data, setData] = useState<FunnelResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const windowHours = resolveWindowHours(range);

  // Monotonic request id: every load bumps it, and any await that resumes to
  // find itself stale writes NOTHING — not data, not error, not the loading
  // flags. apiService.get cannot carry an abort signal, so a superseded GET
  // still completes server-side (a harmless read); what matters is that its
  // resolution can no longer overwrite a newer request's result. This was the
  // page's worst defect: the 12x15s retry loop could set minutes-stale data
  // over fresh, and typing a year in the date picker fired a refetch per
  // keystroke, letting the page settle on whichever response landed last.
  const reqSeq = useRef(0);
  // Skeletons only before the FIRST data arrives; after that every reload is
  // a soft refresh (chip + disabled controls, content stays visible). This is
  // one policy for the header filters, the ledger's inline filters, and the
  // refresh icon — the old split gave the ledger full-page skeletons.
  const hasDataRef = useRef(false);

  const load = useCallback(async (isRefresh = false) => {
    const seq = ++reqSeq.current;
    const stale = () => reqSeq.current !== seq;
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const wh = resolveWindowHours(range);
      let endpoint = `/api/admin/activation-funnel?range=${toApiToken(range)}&window_hours=${wh}`;
      if (range.type === 'custom') {
        // The endpoint 400s on a malformed custom range; the shared filter only
        // ever yields two well-formed, ordered dates, so one can never be sent.
        const { from, to } = resolveRangeYmd(range);
        endpoint += `&from=${from}&to=${to}`;
      }

      const maxAttempts = 12;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const result = await (apiService as any).get(endpoint);
          if (stale()) return;
          hasDataRef.current = true;
          setData(result.data);
          setError(null);
          return;
        } catch (err: unknown) {
          if (stale()) return;
          const msg = err instanceof Error ? err.message : String(err);
          const is502 = /502|bad gateway/i.test(msg);
          if (is502 && attempt < maxAttempts - 1) {
            // Production copy: an operator on admin.trylifescribe.com has no
            // Docker and no localhost. The retry SEMANTICS are unchanged.
            setError('The admin API is temporarily unavailable. Retrying…');
            await new Promise((resolve) => setTimeout(resolve, 15000));
            if (stale()) return;
            continue;
          }
          const authFailed = /unauthorized|authentication|token|401/i.test(msg);
          setError(
            authFailed
              ? 'Your session has expired. Sign out and sign back in, then try again.'
              : is502
                ? 'The admin API is still unavailable after several retries. Click Retry, or try again in a few minutes.'
                : `Couldn't load the activation funnel: ${msg}`,
          );
          return;
        }
      }
    } finally {
      if (!stale()) {
        if (isRefresh) setRefreshing(false); else setLoading(false);
      }
    }
  }, [range]);

  // Load on mount + range changes. The half-filled-custom debounce this used to
  // need is gone with the date TextFields: the shared filter applies a custom
  // span only once both dates are valid and ordered.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load(hasDataRef.current);
  }, [range]);

  const scrollToLedger = () => {
    document.getElementById('leak-ledger')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box sx={{ px: 2, py: 2.5 }}>
     <Box sx={{ maxWidth: PAGE_MAX_WIDTH, mx: 'auto', width: '100%' }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontSize: '11px', color: '#9CA3AF', mb: 0.25 }}>
            Analytics &amp; Insights → User Activation Funnel
          </Typography>
          <Typography variant="h4" sx={{ fontFamily: FONT_H, fontWeight: 700, color: '#1A5F7A' }}>
            User Activation Funnel
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#5D6D7E', mt: 0.75, maxWidth: 760, lineHeight: 1.5 }}>
            The 30,000-foot view: <b>who is in the funnel</b>, <b>how far they get</b>, then <b>where exactly they fall off</b>. Read top to bottom — each section narrows the question until the Leak Ledger answers it.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <TimeRangeFilter
            value={range}
            onChange={setRange}
            onRefresh={() => load(true)}
            refreshing={refreshing}
          />
          {/* Epoch clamping is a server fact about the window that was actually
              measured — it must survive the control swap, in both flavours. */}
          {range.type === 'custom' && data?.range_meta?.epoch_clamped && (
            <Typography sx={{ fontFamily: FONT, fontSize: '11px', color: '#9A3412', maxWidth: 360, textAlign: 'right', lineHeight: 1.4 }}>
              Effective from {data.range_meta.effective_from} (funnel epoch {data.range_meta.funnel_epoch}) — earlier dates are excluded.
            </Typography>
          )}
          {range.type !== 'custom' && data?.range_meta?.epoch_clamped && (
            <Typography sx={{ fontFamily: FONT, fontSize: '11px', color: '#9A3412', maxWidth: 360, textAlign: 'right', lineHeight: 1.4 }}>
              Showing {data.range_meta.effective_from} → {data.range_meta.effective_to} (clamped to funnel epoch).
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── Error state ──────────────────────────────────────────────────
          With data on screen this is a BANNER above live content — a
          transient retry used to blank all six sections for up to three
          minutes. Only a failure before the first load blocks the page. */}
      {error && (
        <Paper elevation={0} sx={{ border: '1px solid #FCA5A5', borderRadius: '12px', p: data ? 1.5 : 3, mb: 3, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#B0202F', mb: data ? 0.75 : 1.5 }}>
            {error}{data ? ' — showing the last loaded data.' : ''}
          </Typography>
          <Button variant="outlined" color="error" size="small" onClick={() => load(Boolean(data))}>Retry</Button>
        </Paper>
      )}

      {/* ── 1. Milestone Funnel ─────────────────────────────────────────── */}
      {(!error || data) && (
        <MilestoneFunnel
          milestones={data?.milestones ?? []}
          funnelTop={data?.funnel_top ?? 0}
          downloadClicks={data?.download_clicks ?? 0}
          loading={loading}
        />
      )}

      {(!error || data) && (
        <JourneyFunnelSummaryStrip range={range} />
      )}

      {/* ── 2. Acquisition Mix ──────────────────────────────────────────── */}
      {(!error || data) && (
        <AcquisitionMix
          methods={data?.auth_method_breakdown ?? []}
          funnelTop={data?.funnel_top ?? 0}
          loading={loading}
        />
      )}

      {/* ── 3. Phase-to-Phase Conversion ────────────────────────────────── */}
      {(!error || data) && (
        <PhaseConversionStaircase
          phaseFlow={data?.phase_flow ?? []}
          funnelTop={data?.funnel_top ?? 0}
          loading={loading}
          onOpenLedger={scrollToLedger}
        />
      )}

      {/* ── 4. Leak Ledger ──────────────────────────────────────────────── */}
      {(!error || data) && (
        <LeakLedger
          leaks={data?.leak_ledger ?? []}
          loading={loading}
          refreshing={refreshing}
          range={range}
          rangeMeta={data?.range_meta ?? null}
          windowHours={windowHours}
        />
      )}

      {/* ── Phase Flow — Heat Strip (retained funnel card) ──────────────── */}
      {(!error || data) && (
        <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', p: 2, mt: 4 }}>
          {/* Card header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
            <Box>
              <Typography component="h2" sx={{ fontFamily: FONT_H, fontSize: '19px', fontWeight: 700, color: '#1A5F7A', lineHeight: 1.15 }}>
                Phase Flow — Heat Strip
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: '12.5px', color: '#5D6D7E', mt: 0.5, maxWidth: 760, lineHeight: 1.5 }}>
                Color-intensity view of how each phase retains its share of new Members, step by step; darker = healthier, warmer = where you&apos;re bleeding.
              </Typography>
              {data && (
                <Typography sx={{ fontFamily: FONT, fontSize: '10px', color: '#9CA3AF', mt: 0.5 }}>
                  {data.funnel_top.toLocaleString()} new Members baseline · {new Date(data.generated_at).toLocaleTimeString()}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
              {/* View toggle */}
              <Box sx={{ display: 'flex', bgcolor: '#F3F4F6', borderRadius: '8px', p: 0.5 }}>
                {(['heatstrip', 'cascade'] as const).map(v => (
                  <Button key={v} size="small" onClick={() => setView(v)}
                    sx={{
                      fontFamily: FONT, fontSize: '11px', textTransform: 'none',
                      borderRadius: '6px', px: 1.5,
                      bgcolor: view === v ? '#fff' : 'transparent',
                      boxShadow: view === v ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                      color: view === v ? '#111827' : '#6B7280',
                      fontWeight: view === v ? 700 : 400,
                    }}
                  >
                    {v === 'heatstrip' ? 'Heatstrip' : 'Cascade'}
                  </Button>
                ))}
              </Box>
              {/* Band legend */}
              <BandLegend />
            </Box>
          </Box>

          {/* Funnel content */}
          {loading ? (
            <Box>
              {Array(5).fill(null).map((_, i) => (
                <Box key={i} sx={{ mb: 2 }}>
                  <Skeleton variant="text" width={200} height={18} sx={{ mb: 1 }} />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {Array(4).fill(null).map((_, j) => (
                      <Skeleton key={j} variant="rounded" width={160} height={80} sx={{ borderRadius: '8px' }} />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : data ? (
            view === 'heatstrip' ? (
              <FunnelHeatstrip
                phases={data.phases}
                funnelTop={data.funnel_top}
                authMethods={data.auth_method_breakdown}
              />
            ) : (
              <FunnelCascade
                phases={data.phases}
                funnelTop={data.funnel_top}
                authMethods={data.auth_method_breakdown}
              />
            )
          ) : null}
        </Paper>
      )}
     </Box>
    </Box>
  );
};

export default MmpAnalyticsPage;
